import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, Leaf, Mountain, Compass, Map } from 'lucide-react';

const DartmouthCollege: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Dartmouth College";

    return (
        <div className="min-h-screen bg-white font-serif text-[#00693E] selection:bg-[#00693E] selection:text-white">
            {/* Header */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-[#00693E] sticky top-0 z-50">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-3xl font-serif text-[#00693E] tracking-tight">{schoolName.split(' ')[0]}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-sans font-bold text-[#00693E] uppercase text-xs tracking-widest">
                        <a href="#" className="hover:text-[#004225]">Admissions</a>
                        <a href="#" className="hover:text-[#004225]">Academics</a>
                        <a href="#" className="hover:text-[#004225]">Research</a>
                        <a href="#" className="hover:text-[#004225]">Campus Life</a>
                        <a href="#" className="hover:text-[#004225]">Arts</a>
                        <a href="#" className="hover:text-[#004225]">Athletics</a>
                    </div>

                    <div className="flex gap-4">
                        <Search size={22} className="text-[#00693E] cursor-pointer" />
                        <Menu size={24} className="text-[#00693E] cursor-pointer lg:hidden" />
                    </div>
                </div>
            </nav>

            {/* Hero - Outdoors / Green */}
            <header className="relative h-[650px] bg-[#00693E]">
                <img 
                    src="https://images.unsplash.com/photo-1588007375246-3ee823ef4851?q=80&w=1974&auto=format&fit=crop" 
                    alt="Dartmouth Green" 
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#004225]/90"></div>
                
                <div className="absolute bottom-0 w-full p-12 md:p-20 text-white text-center">
                    <h1 className="text-5xl md:text-7xl font-serif mb-8 drop-shadow-lg">
                        A Voice in the Wilderness.
                    </h1>
                    <p className="text-xl max-w-2xl mx-auto mb-12 font-sans font-light drop-shadow-md">
                        A profound sense of place. A tight-knit community. An adventurous spirit. 
                    </p>
                    <Button className="bg-white text-[#00693E] hover:bg-gray-100 font-sans font-bold uppercase tracking-widest px-10 h-14 rounded-sm text-sm border border-transparent">
                        Discover {schoolName.split(' ')[0]}
                    </Button>
                </div>
            </header>

            {/* Spirit of Place */}
            <section className="py-24 px-6 md:px-12 bg-[#F7F9F8]">
                <div className="max-w-[1000px] mx-auto text-center">
                    <span className="text-[#00693E] font-sans font-bold uppercase tracking-widest text-xs mb-6 block">The {schoolName.split(' ')[0]} Experience</span>
                    <h2 className="text-3xl md:text-4xl font-serif text-[#222] mb-12 leading-tight">
                        We are a fusion of a renowned liberal arts college and a robust research university, where students and faculty partner to take on the world's great challenges.
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 font-sans">
                         <div className="flex flex-col items-center gap-4">
                             <Leaf size={40} className="text-[#00693E]" />
                             <h3 className="font-bold uppercase tracking-widest text-sm text-[#222]">Sustainability</h3>
                             <p className="text-gray-600 text-sm max-w-xs">Leading the way in energy and environmental solutions.</p>
                         </div>
                         <div className="flex flex-col items-center gap-4">
                             <Mountain size={40} className="text-[#00693E]" />
                             <h3 className="font-bold uppercase tracking-widest text-sm text-[#222]">The Outdoors</h3>
                             <p className="text-gray-600 text-sm max-w-xs">A basecamp for learning and adventure in New Hampshire.</p>
                         </div>
                         <div className="flex flex-col items-center gap-4">
                             <Compass size={40} className="text-[#00693E]" />
                             <h3 className="font-bold uppercase tracking-widest text-sm text-[#222]">Global Reach</h3>
                             <p className="text-gray-600 text-sm max-w-xs">Programs and partnerships in more than 50 countries.</p>
                         </div>
                    </div>
                </div>
            </section>

             {/* Mosaic / Grid */}
             <section className="bg-white">
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 h-auto md:h-[600px]">
                     <div className="bg-[#004225] text-white p-12 flex flex-col justify-center">
                         <h3 className="text-3xl font-serif mb-6">Guarini School of Graduate and Advanced Studies</h3>
                         <a href="#" className="font-sans font-bold uppercase tracking-widest text-xs border-b border-white pb-1 w-fit hover:opacity-80">Learn More</a>
                     </div>
                     <div className="relative group overflow-hidden">
                         <img src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Students" />
                     </div>
                     <div className="bg-[#222] text-white p-12 flex flex-col justify-center">
                         <h3 className="text-3xl font-serif mb-6">Geisel School of Medicine</h3>
                         <a href="#" className="font-sans font-bold uppercase tracking-widest text-xs border-b border-white pb-1 w-fit hover:opacity-80">Learn More</a>
                     </div>
                     <div className="relative group overflow-hidden">
                         <img src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Research" />
                     </div>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 h-auto md:h-[600px]">
                     <div className="relative group overflow-hidden lg:order-1">
                         <img src="https://images.unsplash.com/photo-1505664194779-8beaceb93744?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Engineering" />
                     </div>
                     <div className="bg-[#00693E] text-white p-12 flex flex-col justify-center lg:order-2">
                         <h3 className="text-3xl font-serif mb-6">Thayer School of Engineering</h3>
                         <a href="#" className="font-sans font-bold uppercase tracking-widest text-xs border-b border-white pb-1 w-fit hover:opacity-80">Learn More</a>
                     </div>
                     <div className="relative group overflow-hidden lg:order-3">
                         <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=2032&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Business" />
                     </div>
                     <div className="bg-[#222] text-white p-12 flex flex-col justify-center lg:order-4">
                         <h3 className="text-3xl font-serif mb-6">Tuck School of Business</h3>
                         <a href="#" className="font-sans font-bold uppercase tracking-widest text-xs border-b border-white pb-1 w-fit hover:opacity-80">Learn More</a>
                     </div>
                 </div>
             </section>

            {/* Footer */}
            <footer className="bg-white text-[#222] py-20 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 font-sans text-sm">
                    <div className="col-span-1 md:col-span-2">
                        <span className="font-serif text-3xl text-[#00693E] mb-6 block">{schoolName.split(' ')[0]}</span>
                        <p className="text-gray-600 mb-2">Hanover, New Hampshire 03755</p>
                        <p className="text-gray-600">(603) 646-1110</p>
                        <div className="flex gap-4 mt-8">
                             <a href="#" className="text-[#00693E] hover:underline">Maps & Directions</a>
                             <a href="#" className="text-[#00693E] hover:underline">Contact Us</a>
                        </div>
                    </div>
                    
                    <div>
                        <ul className="space-y-3 font-bold text-[#00693E] uppercase tracking-wide text-xs">
                             <li><a href="#" className="hover:underline">Work at {schoolName.split(' ')[0]}</a></li>
                             <li><a href="#" className="hover:underline">Visit {schoolName.split(' ')[0]}</a></li>
                             <li><a href="#" className="hover:underline">{schoolName.split(' ')[0]} News</a></li>
                             <li><a href="#" className="hover:underline">Emergency</a></li>
                        </ul>
                    </div>
                     <div>
                        <ul className="space-y-3 font-bold text-[#00693E] uppercase tracking-wide text-xs">
                             <li><a href="#" className="hover:underline">Accessibility</a></li>
                             <li><a href="#" className="hover:underline">Privacy</a></li>
                             <li><a href="#" className="hover:underline">Copyright</a></li>
                             <li><a href="#" className="hover:underline">Title IX</a></li>
                        </ul>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default DartmouthCollege;
